/**
 * Product and promotion access policy.
 *
 * Keeps the legacy `window.calculateProductRestrictions(...)` API intact while
 * exposing normalized product/promo evaluators for newer dynamic surfaces.
 */
(function(window, document) {
  'use strict';

  var PRODUCT_MESSAGES = {
    login_required: 'Login to see prices',
    account_not_approved: 'Your account has not been approved. If it has been more than 3 days since registering, please contact us.',
    tag_restricted: 'Contact us for information',
    region_prairie: 'Not available in Prairie Region',
    region_bc: 'Not available in BC Region',
    region_ontario: 'Not available in Ontario',
    region_new_brunswick: 'Not available in New Brunswick',
    region_atlantic: 'Not available in Atlantic Canada',
    region_east: 'Not available in Eastern Canada',
    region_qc: 'Not available in Quebec Region'
  };

  var PROMO_MESSAGES = {
    not_logged_in: 'Login to view pricing',
    account_not_approved: 'Your account is pending approval. Contact us for access.',
    brand_restricted: 'This brand requires special authorization. Contact us for access.',
    professional_required: 'Hair color products require professional salon account.'
  };

  function toBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return Boolean(value);
  }

  function splitTags(value) {
    if (Array.isArray(value)) {
      return value.filter(function(tag) {
        return tag !== null && tag !== undefined && String(tag).trim() !== '';
      }).map(function(tag) {
        return String(tag).trim();
      });
    }

    if (typeof value !== 'string') return [];

    var trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return splitTags(parsed);
      } catch (e) {
        // Fall back to comma splitting below.
      }
    }

    return trimmed.split(',').filter(function(tag) {
      return tag.trim() !== '';
    }).map(function(tag) {
      return tag.trim();
    });
  }

  function parseEncodedTags(value) {
    if (!value) return null;
    if (Array.isArray(value)) return value;

    try {
      return JSON.parse(decodeURIComponent(String(value).replace(/\+/g, '%20')));
    } catch (e) {
      return null;
    }
  }

  function normalizeTags(value) {
    return splitTags(value).map(function(tag) {
      return tag.toLowerCase();
    });
  }

  function normalizeAccessTags(value) {
    if (!Array.isArray(value)) {
      return normalizeTags(value);
    }

    return value.filter(function(tag) {
      return tag !== null && tag !== undefined && String(tag).trim() !== '';
    }).map(function(tag) {
      if (tag && typeof tag === 'object') {
        return tag;
      }
      return String(tag).trim().toLowerCase();
    });
  }

  function tagStringFrom(tags, fallback) {
    if (typeof fallback === 'string' && fallback.trim() !== '') {
      return fallback.toLowerCase();
    }
    return normalizeTags(tags).join(',');
  }

  function tagListContains(tags, needle) {
    var required = String(needle || '').toLowerCase();
    if (!required) return false;

    var list = Array.isArray(tags) ? tags : splitTags(tags);
    return list.some(function(tag) {
      return cleanTagValue(tag).some(function(candidate) {
        return candidate.indexOf(required) !== -1;
      });
    });
  }

  function tagListHasExact(tags, needle) {
    var required = String(needle || '').trim().toLowerCase();
    if (!required) return false;

    var list = Array.isArray(tags) ? tags : splitTags(tags);
    return list.some(function(tag) {
      return cleanTagValue(tag).some(function(candidate) {
        return candidate === required;
      });
    });
  }

  function customerBucketsContain(customer, buckets, needle) {
    return buckets.some(function(bucket) {
      return tagListContains(customer[bucket], needle);
    });
  }

  function customerBucketsHaveExact(customer, buckets, needle) {
    return buckets.some(function(bucket) {
      return tagListHasExact(customer[bucket], needle);
    });
  }

  function hasSalonAccessTag(tags) {
    return tagListContains(tags, 'hair salon') ||
      tagListContains(tags, 'hairsty/barber');
  }

  function readHeaderCustomerContext() {
    var headerWrapper = document.getElementById('HeaderWrapper');
    if (!headerWrapper) return null;

    var contextAttr = headerWrapper.getAttribute('data-customer-context');
    if (contextAttr) {
      try {
        return JSON.parse(contextAttr);
      } catch (e) {
        // Continue to the legacy attributes.
      }
    }

    return {
      tags: headerWrapper.getAttribute('data-customer') || '',
      isLoggedIn: headerWrapper.getAttribute('data-customer-logged-in') === 'true'
    };
  }

  function readHeaderLoggedIn() {
    var headerWrapper = document.getElementById('HeaderWrapper');
    if (!headerWrapper) return null;
    var loggedInAttr = headerWrapper.getAttribute('data-customer-logged-in');
    if (loggedInAttr === null) return null;
    return loggedInAttr === 'true';
  }

  var cachedHeaderCustomerKey = null;
  var cachedHeaderCustomerContext = null;

  function getHeaderCustomerCacheKey() {
    var headerWrapper = document.getElementById('HeaderWrapper');
    if (!headerWrapper) return null;

    return [
      headerWrapper.getAttribute('data-customer-context') || '',
      headerWrapper.getAttribute('data-customer') || '',
      headerWrapper.getAttribute('data-customer-logged-in') || ''
    ].join('||');
  }

  function getCachedHeaderCustomerContext() {
    var cacheKey = getHeaderCustomerCacheKey();
    if (cacheKey === null) {
      return normalizeCustomer(readHeaderCustomerContext());
    }

    if (cachedHeaderCustomerContext && cachedHeaderCustomerKey === cacheKey) {
      return cachedHeaderCustomerContext;
    }

    cachedHeaderCustomerKey = cacheKey;
    cachedHeaderCustomerContext = normalizeCustomer(readHeaderCustomerContext());
    return cachedHeaderCustomerContext;
  }

  function normalizeCustomer(customerInput, isLoggedIn) {
    var source = customerInput;
    if (source === null || source === undefined || source === '') {
      source = readHeaderCustomerContext();
    }

    if (source && typeof source === 'object' && !Array.isArray(source) && source.__ibNormalizedCustomer === true && typeof isLoggedIn !== 'boolean') {
      return source;
    }

    var explicitLoggedIn = typeof isLoggedIn === 'boolean' ? isLoggedIn : null;
    var tags = [];
    var rawTagString = '';
    var brandTags = [];
    var professionalTags = [];
    var regionTags = [];
    var loggedIn = false;
    var hasBluelinkTag = false;
    var isSalonCustomer = false;

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      tags = splitTags(source.tags || source.rawTags || source.allTags || source.customerTags || source.customer_tags);
      rawTagString = tagStringFrom(tags, source.customerTagsString || source.allTagsString || source.customerTags || source.customer_tags);
      brandTags = normalizeAccessTags(source.brandTags || source.customerBrandTags || source.customer_brand_tags);
      professionalTags = normalizeAccessTags(source.professionalTags || source.customerProfessionalTags || source.customer_professional_tags);
      regionTags = normalizeAccessTags(source.regionTags || source.customerRegionTags || source.customer_region_tags);

      if (explicitLoggedIn !== null) {
        loggedIn = explicitLoggedIn;
      } else if ('isLoggedIn' in source) {
        loggedIn = toBoolean(source.isLoggedIn);
      } else if ('loggedIn' in source) {
        loggedIn = toBoolean(source.loggedIn);
      } else {
        var headerLoggedIn = readHeaderLoggedIn();
        loggedIn = headerLoggedIn !== null ? headerLoggedIn : rawTagString !== '';
      }

      if ('hasBluelinkTag' in source) {
        hasBluelinkTag = toBoolean(source.hasBluelinkTag);
      } else if ('approved' in source) {
        hasBluelinkTag = toBoolean(source.approved);
      } else {
        hasBluelinkTag = rawTagString.indexOf('bluelinktagged') !== -1;
      }

      if ('isSalonCustomer' in source) {
        isSalonCustomer = toBoolean(source.isSalonCustomer);
      } else {
        isSalonCustomer = hasSalonAccessTag(rawTagString) || hasSalonAccessTag(professionalTags);
      }
    } else {
      tags = splitTags(source);
      rawTagString = tagStringFrom(tags, source);
      var headerLoggedInFallback = readHeaderLoggedIn();
      loggedIn = explicitLoggedIn !== null ? explicitLoggedIn : (headerLoggedInFallback !== null ? headerLoggedInFallback : rawTagString !== '');
      hasBluelinkTag = rawTagString.indexOf('bluelinktagged') !== -1;
      isSalonCustomer = hasSalonAccessTag(rawTagString);
    }

    if (!brandTags.length) {
      brandTags = normalizeTags(tags).filter(function(tag) {
        return tag.indexOf('brand:') !== -1;
      });
    }
    if (!professionalTags.length) {
      professionalTags = normalizeTags(tags).filter(function(tag) {
        return tag === 'hair salon' || tag.indexOf('hairsty/barber') !== -1;
      });
    }
    if (!regionTags.length) {
      regionTags = normalizeTags(tags).filter(function(tag) {
        return tag.indexOf('region:') !== -1;
      });
    }

    var normalizedCustomer = {
      isLoggedIn: loggedIn,
      loggedIn: loggedIn,
      hasBluelinkTag: hasBluelinkTag,
      approved: hasBluelinkTag,
      isSalonCustomer: isSalonCustomer,
      tags: normalizeTags(tags),
      rawTags: tags,
      tagString: rawTagString,
      customerTags: rawTagString,
      brandTags: brandTags,
      customerBrandTags: brandTags,
      professionalTags: professionalTags,
      customerProfessionalTags: professionalTags,
      regionTags: regionTags,
      customerRegionTags: regionTags
    };
    Object.defineProperty(normalizedCustomer, '__ibNormalizedCustomer', {
      value: true
    });
    return normalizedCustomer;
  }

  function getProductTags(productOrTags) {
    if (productOrTags && typeof productOrTags === 'object' && !Array.isArray(productOrTags)) {
      return normalizeTags(productOrTags.tags || productOrTags.productTags);
    }
    return normalizeTags(productOrTags);
  }

  function buildProductResult(flags, showSalonDiscount, reason) {
    var hideRegional = flags.hideprairie || flags.hidebc || flags.hideontario || flags.hidenewbrunswick || flags.hideatlantic || flags.hideeast || flags.hideqc;
    var hideTags = flags.hidebalmain || flags.hidekm || flags.hideeufora || flags.hideaz || flags.hidehair;
    var hidePrice = flags.hidelogin || hideTags || hideRegional || flags.showblinkmsg;
    var message = reason ? PRODUCT_MESSAGES[reason] : '';

    return {
      canViewPrice: !hidePrice,
      canAddToCart: !hidePrice,
      hideFromGrid: hideRegional,
      reason: reason || null,
      message: message,
      showSalonDiscount: showSalonDiscount,

      hideprice: hidePrice,
      hidelogin: flags.hidelogin,
      hidetags: hideTags,
      showblinkmsg: flags.showblinkmsg,
      show_salon_discount: showSalonDiscount,
      restriction_message: message,
      hide_regional: hideRegional,

      hidebalmain: flags.hidebalmain,
      hidekm: flags.hidekm,
      hideeufora: flags.hideeufora,
      hideaz: flags.hideaz,
      hidehair: flags.hidehair,
      hideprairie: flags.hideprairie,
      hidebc: flags.hidebc,
      hideontario: flags.hideontario,
      hidenewbrunswick: flags.hidenewbrunswick,
      hideatlantic: flags.hideatlantic,
      hideeast: flags.hideeast,
      hideqc: flags.hideqc
    };
  }

  function evaluateProductAccess(productOrTags, customerInput, isLoggedIn) {
    var pTags = getProductTags(productOrTags);
    var customer = normalizeCustomer(customerInput, isLoggedIn);

    var logintoseetag = pTags.some(function(t) { return t.indexOf('logintosee') !== -1; });
    var balmainprod = pTags.some(function(t) { return t.indexOf('fbrand:balmain') !== -1; });
    var kmprod = pTags.some(function(t) { return t.indexOf('fbrand:km') !== -1; });
    var euforaprod = pTags.some(function(t) { return t.indexOf('fbrand:eufora') !== -1; });
    var azprod = pTags.some(function(t) { return t.indexOf('restrict:az') !== -1; });
    var resthaircolor = pTags.some(function(t) { return t.indexOf('restrict:hair color') !== -1; });
    var resteast = pTags.some(function(t) { return t === 'restrict:east'; });
    var restbc = pTags.some(function(t) { return t === 'restrict:bc'; });
    var restprairie = pTags.some(function(t) { return t === 'restrict:prairie'; });
    var restontario = pTags.some(function(t) { return t === 'restrict:ontario'; });
    var restnewbrunswick = pTags.some(function(t) { return t === 'restrict:new-brunswick'; });
    var restatlantic = pTags.some(function(t) { return t === 'restrict:atlantic'; });
    var restqc = pTags.some(function(t) { return t === 'restrict:qc'; });
    var matrixprod = pTags.some(function(t) { return t === 'matrix'; });

    var balmaincust = tagListContains(customer.brandTags, 'brand:balmain');
    var kmcust = tagListContains(customer.brandTags, 'brand:km');
    var euforacust = tagListContains(customer.brandTags, 'brand:eufora');
    var azcust = tagListContains(customer.brandTags, 'brand:az');
    var hairsalon = customerBucketsContain(customer, ['professionalTags', 'tags'], 'hair salon');
    var hairColorApproved = customer.isSalonCustomer || hasSalonAccessTag(customer.professionalTags) || hasSalonAccessTag(customer.tags);
    var regeast = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:east');
    var regbc = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:bc') ||
      customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:вс');
    var regprairie = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:prairie');
    var regontario = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:ontario') ||
      customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'ontario');
    var regnewbrunswick = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:new-brunswick');
    var regatlantic = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:atlantic');
    var regqc = customerBucketsHaveExact(customer, ['regionTags', 'tags'], 'region:quebec');
    var blink = customer.hasBluelinkTag || tagListContains(customer.tags, 'bluelinktagged');
    var matrixoverride = tagListContains(customer.tags, 'matrixoverride');

    var salonDiscountTags = ['eleven salon pricing', 'neu10', 'ethica'];
    var hasSalonDiscountTag = pTags.some(function(t) {
      return salonDiscountTags.indexOf(t) !== -1;
    });
    var showSalonDiscount = hairsalon && hasSalonDiscountTag;

    var flags = {
      hidelogin: false,
      hidebalmain: false,
      hidekm: false,
      hideeufora: false,
      hideaz: false,
      hidehair: false,
      hideprairie: false,
      hidebc: false,
      hideontario: false,
      hidenewbrunswick: false,
      hideatlantic: false,
      hideeast: false,
      hideqc: false,
      showblinkmsg: false
    };

    if (logintoseetag) {
      if (customer.isLoggedIn) {
        if (balmainprod && !balmaincust) flags.hidebalmain = true;
        if (kmprod && !kmcust) flags.hidekm = true;
        if (euforaprod && !euforacust) flags.hideeufora = true;
        if (azprod && !azcust) flags.hideaz = true;
        if (resthaircolor) flags.hidehair = !hairColorApproved;
      } else {
        flags.hidelogin = true;
      }
    }

    if (resteast && regeast) flags.hideeast = true;
    if (restbc && regbc) flags.hidebc = true;
    if (restprairie && regprairie) flags.hideprairie = true;
    if (restontario && regontario) flags.hideontario = true;
    if (restnewbrunswick && regnewbrunswick) flags.hidenewbrunswick = true;
    if (restatlantic && regatlantic) flags.hideatlantic = true;
    if (restqc && regqc) flags.hideqc = true;

    if (matrixprod && matrixoverride) {
      flags.hideprairie = false;
      flags.hidebc = false;
      flags.hideontario = false;
      flags.hidenewbrunswick = false;
      flags.hideatlantic = false;
      flags.hideeast = false;
      flags.hideqc = false;
    }

    if (logintoseetag && customer.isLoggedIn && !blink) {
      flags.showblinkmsg = true;
    }

    var hideTags = flags.hidebalmain || flags.hidekm || flags.hideeufora || flags.hideaz || flags.hidehair;
    var reason = null;
    if (flags.hidelogin) {
      reason = 'login_required';
    } else if (flags.showblinkmsg) {
      reason = 'account_not_approved';
    } else if (hideTags) {
      reason = 'tag_restricted';
    } else if (flags.hideprairie) {
      reason = 'region_prairie';
    } else if (flags.hidebc) {
      reason = 'region_bc';
    } else if (flags.hideontario) {
      reason = 'region_ontario';
    } else if (flags.hidenewbrunswick) {
      reason = 'region_new_brunswick';
    } else if (flags.hideatlantic) {
      reason = 'region_atlantic';
    } else if (flags.hideeast) {
      reason = 'region_east';
    } else if (flags.hideqc) {
      reason = 'region_qc';
    }

    return buildProductResult(flags, showSalonDiscount, reason);
  }

  function cleanTagValue(tag) {
    if (tag && typeof tag === 'object') {
      return Object.keys(tag).concat(Object.values(tag)).map(function(value) {
        return String(value).toLowerCase();
      });
    }
    var value = String(tag || '').toLowerCase();
    // Some legacy encoded tag values are prefixed with []; keep both forms for matching.
    return [value.indexOf('[]') === 0 ? value.substring(2) : value, value];
  }

  function hasCustomerTag(customer, requiredTag) {
    var required = String(requiredTag || '').toLowerCase();
    return customer.brandTags.some(function(tag) {
      return cleanTagValue(tag).indexOf(required) !== -1;
    });
  }

  function getPromotionCategories(promo, settings) {
    if (settings && typeof settings.getPromotionCategories === 'function') {
      return settings.getPromotionCategories(promo) || [];
    }
    if (!promo) return [];
    if (Array.isArray(promo.categories)) return promo.categories;
    if (Array.isArray(promo.category)) return promo.category;
    if (promo.category) return [promo.category];
    return [];
  }

  function promotionResult(allowed, restrictionType, message) {
    return {
      allowed: allowed,
      restrictionType: restrictionType || null,
      message: message || null,
      canViewPrice: allowed,
      canAddToCart: allowed,
      reason: restrictionType || null
    };
  }

  function evaluatePromotionAccess(promo, customerInput, settings) {
    settings = settings || {};
    var restrictionsEnabled = toBoolean(settings.pricingRestrictionsEnabled || settings.enablePricingRestrictions);
    if (!restrictionsEnabled) {
      return promotionResult(true);
    }

    var customer = normalizeCustomer(customerInput);
    if (!customer.isLoggedIn) {
      return promotionResult(false, 'not_logged_in', PROMO_MESSAGES.not_logged_in);
    }

    if (!customer.hasBluelinkTag) {
      return promotionResult(false, 'account_not_approved', PROMO_MESSAGES.account_not_approved);
    }

    var brandMapping = {
      'balmain': 'brand:balmain',
      'kevin murphy': 'brand:km',
      'eufora': 'brand:eufora'
    };
    var brandLower = promo && promo.brand ? String(promo.brand).toLowerCase() : '';
    var requiredTag = brandMapping[brandLower];
    if (requiredTag && !hasCustomerTag(customer, requiredTag)) {
      return promotionResult(false, 'brand_restricted', PROMO_MESSAGES.brand_restricted);
    }

    var categories = getPromotionCategories(promo, settings);
    var hasHairColor = categories.some(function(category) {
      return String(category || '').toLowerCase().indexOf('hair color') !== -1;
    });

    var hairColorPromoApproved = customer.isSalonCustomer ||
      hasSalonAccessTag(customer.professionalTags) ||
      hasSalonAccessTag(customer.tags);

    if (hasHairColor && !hairColorPromoApproved) {
      return promotionResult(false, 'professional_required', PROMO_MESSAGES.professional_required);
    }

    return promotionResult(true);
  }

  var policy = window.IBAccessPolicy || {};
  policy.normalizeCustomer = normalizeCustomer;
  policy.getCustomerContext = getCachedHeaderCustomerContext;
  policy.parseTags = parseEncodedTags;
  policy.evaluateProductAccess = evaluateProductAccess;
  policy.evaluatePromotionAccess = evaluatePromotionAccess;
  policy.messages = {
    product: PRODUCT_MESSAGES,
    promotion: PROMO_MESSAGES
  };

  window.IBAccessPolicy = policy;

  window.calculateProductRestrictions = function(productTags, customerTags, isLoggedIn) {
    return evaluateProductAccess(productTags, customerTags, isLoggedIn);
  };
})(window, document);
